import { useState, useRef, useCallback } from 'react';

const MENU_ORDER_KEY = 'gym_notebook_menu_order';

// Default menu order
const DEFAULT_MENU_ORDER = [
  'nutrition',
  'walking',
  'aiplanner',
  'fridge',
  'healthcoach',
  'medications',
  'diabetes',
  'community',
  'photos',
  'calendar',
  'statistics',
  'wearables'
];

/**
 * Custom hook to manage menu order with drag-and-drop support
 */
export function useMenuOrder() {
  const [menuOrder, setMenuOrder] = useState(() => {
    try {
      const saved = localStorage.getItem(MENU_ORDER_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Ensure all default items are present
        const combined = [...parsed];
        DEFAULT_MENU_ORDER.forEach(item => {
          if (!combined.includes(item)) {
            combined.push(item);
          }
        });
        return combined;
      }
    } catch (e) {
      console.error('Error loading menu order:', e);
    }
    return DEFAULT_MENU_ORDER;
  });

  const [isEditMode, setIsEditMode] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);

  // Touch handling refs
  const touchStartY = useRef(0);
  const touchCurrentItem = useRef(null);

  const saveMenuOrder = useCallback((newOrder) => {
    setMenuOrder(newOrder);
    localStorage.setItem(MENU_ORDER_KEY, JSON.stringify(newOrder));
  }, []);

  const handleDragStart = useCallback((e, itemId) => {
    if (!isEditMode) return;
    setDraggedItem(itemId);
    e.dataTransfer.effectAllowed = 'move';
  }, [isEditMode]);

  const handleDragOver = useCallback((e, itemId) => {
    e.preventDefault();
    if (!isEditMode || !draggedItem || draggedItem === itemId) return;
    setDragOverItem(itemId);
  }, [isEditMode, draggedItem]);

  const handleDrop = useCallback((e, targetId) => {
    e.preventDefault();
    if (!isEditMode || !draggedItem || draggedItem === targetId) return;
    
    const newOrder = [...menuOrder];
    const draggedIndex = newOrder.indexOf(draggedItem);
    const targetIndex = newOrder.indexOf(targetId);
    
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedItem);
    
    saveMenuOrder(newOrder);
    setDraggedItem(null);
    setDragOverItem(null);
  }, [isEditMode, draggedItem, menuOrder, saveMenuOrder]);

  const handleTouchStart = useCallback((e, itemId) => {
    if (!isEditMode) return;
    touchStartY.current = e.touches[0].clientY;
    touchCurrentItem.current = itemId;
    setDraggedItem(itemId);
  }, [isEditMode]);

  const handleTouchMove = useCallback((e) => {
    if (!isEditMode || !draggedItem) return;
    e.preventDefault();
    
    const touch = e.touches[0];
    const elements = document.elementsFromPoint(touch.clientX, touch.clientY);
    const menuItem = elements.find(el => el.dataset.menuId && el.dataset.menuId !== draggedItem);
    
    if (menuItem) {
      setDragOverItem(menuItem.dataset.menuId);
    }
  }, [isEditMode, draggedItem]);

  const handleTouchEnd = useCallback(() => {
    if (!isEditMode || !draggedItem || !dragOverItem) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }
    
    const newOrder = [...menuOrder];
    const draggedIndex = newOrder.indexOf(draggedItem);
    const targetIndex = newOrder.indexOf(dragOverItem);
    
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedItem);
    
    saveMenuOrder(newOrder);
    setDraggedItem(null);
    setDragOverItem(null);
    touchCurrentItem.current = null;
  }, [isEditMode, draggedItem, dragOverItem, menuOrder, saveMenuOrder]);

  const resetMenuOrder = useCallback(() => {
    saveMenuOrder(DEFAULT_MENU_ORDER);
  }, [saveMenuOrder]);

  return {
    menuOrder,
    isEditMode,
    setIsEditMode,
    draggedItem,
    dragOverItem,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    resetMenuOrder,
  };
}

export default useMenuOrder;
